import { IMAGES } from "@/lib/brand";
import { EnquiryForm } from "@/components/EnquiryForm";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const services = [
  "Venue & Mandap", "Guest Accommodation", "Décor & Florals", "Photography",
  "Sacred Cuisine", "Music & Rituals", "Pre-Wedding Shoot", "Guest Hospitality",
];

export default function Weddings() {
  return (
    <div data-testid="page-weddings">
      <section className="relative h-[92vh] min-h-[620px] overflow-hidden">
        <img
          src="/images/wedding-hero.jpg"
          alt="Himalayan destination wedding — bride and groom with snow-capped peaks"
          className="absolute inset-0 w-full h-full object-cover kc-hero-image object-[72%_45%] md:object-[center_35%]"
          loading="eager"
        />
        <div className="absolute inset-0 hero-scrim-strong" />
        <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-44 lg:pb-52 text-[#F8F6F2]">
          <div className="overline text-[#D4AF37] kc-fade-up">Weddings in the Himalayas</div>
          <h1 className="h-display text-[44px] sm:text-[56px] md:text-[72px] lg:text-[88px] font-light leading-[1.02] mt-6 max-w-5xl kc-fade-up kc-delay-1 tracking-[-0.025em]" style={{textShadow:"0 2px 32px rgba(0,0,0,0.35)"}}>
            Where sacred mountains <span className="italic text-[#D4AF37]">bless</span> new beginnings.
          </h1>
          <div className="mt-10 flex flex-wrap gap-3 kc-fade-up kc-delay-2">
            <Link to="/weddings/triyuginarayan" className="btn-gold" data-testid="wedding-cta-book">Book Your Triyuginarayan Wedding <ArrowUpRight size={14} /></Link>
            <a href="#enquire" className="btn-ghost-light" data-testid="wedding-cta-celebrate">Celebrate Your Dream Wedding</a>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-10 items-start">
          <div className="col-span-12 md:col-span-4">
            <div className="overline">Intimate Destinations</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-6 font-light leading-[1.05] tracking-[-0.02em]">A vow. A valley. A memory that never fades.</h2>
          </div>
          <p className="col-span-12 md:col-span-7 md:col-start-6 text-ink-soft leading-relaxed text-[15px]">
            A Himalayan wedding is not an event — it is a blessing. From the sacred pheras beside the flowing Mandakini to the mountain sunrise on your first morning as a couple, every detail at THE KC INDIA is arranged with reverence, quietude, and craft.
          </p>
        </div>
      </section>

      {/* Editorial gallery grid */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 pb-24 grid grid-cols-12 gap-4">
        <img src={IMAGES.weddingCouple} alt="Couple in mountains" className="col-span-12 md:col-span-8 h-[520px] object-cover" />
        <img src={IMAGES.mandap} alt="Mandap floral" className="col-span-6 md:col-span-4 h-[520px] object-cover" />
        <img src={IMAGES.roomLuxury} alt="Guest room" className="col-span-6 md:col-span-4 h-[320px] object-cover" />
        <img src={IMAGES.food} alt="Wedding feast" className="col-span-12 md:col-span-8 h-[320px] object-cover" />
      </section>

      {/* ========== FEATURED · TRIYUGINARAYAN ========== */}
      <section className="bg-black-lux text-[#F8F6F2] section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-10 items-center">
          <div className="col-span-12 md:col-span-6 img-zoom order-2 md:order-1">
            <img
              src={IMAGES.heroKedarnath}
              alt="Triyuginarayan Temple where Shiva and Parvati wed"
              className="w-full h-[620px] object-cover"
            />
          </div>
          <div className="col-span-12 md:col-span-6 order-1 md:order-2">
            <div className="overline text-gold">Featured Destination</div>
            <h2 className="font-serif-display text-4xl md:text-6xl mt-6 font-light leading-[1] tracking-[-0.02em]">
              A wedding at the origin <br /><span className="italic text-gold">of all weddings.</span>
            </h2>
            <div className="font-devanagari text-lg mt-4 text-[#F8F6F2]/60">त्रियुगीनारायण · अखंड धूनी</div>
            <p className="mt-8 text-[15.5px] leading-[1.85] text-[#F8F6F2]/80 max-w-lg">
              Triyuginarayan is where Lord Shiva and Goddess Parvati exchanged their sacred vows. The Akhand Dhuni — the eternal flame from that divine wedding — still burns today. Couples take their pheras around this same fire, blessed by three yugas of devotion.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-6 max-w-md">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Distance</div>
                <div className="font-serif-display text-2xl mt-2">25 km from Cottage</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Guests</div>
                <div className="font-serif-display text-2xl mt-2">25 · 60 · 120</div>
              </div>
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/weddings/triyuginarayan" className="btn-gold" data-testid="weddings-triyuginarayan-explore">
                Explore Triyuginarayan Weddings <ArrowUpRight size={14} />
              </Link>
              <Link to="/weddings/triyuginarayan#packages" className="btn-ghost-light" data-testid="weddings-triyuginarayan-packages">
                View Wedding Collections
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-10 items-end mb-16">
            <div className="col-span-12 md:col-span-6">
              <div className="overline">Every Element</div>
              <h2 className="font-serif-display text-5xl md:text-6xl mt-6 font-light leading-[1] tracking-[-0.02em]">Held with care, from first hello to last farewell.</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10">
            {services.map((s, i) => (
              <div key={s} className="bg-cream p-8 md:p-10" data-testid={`wed-service-${i}`}>
                <span className="index-num">№ 0{i + 1}</span>
                <div className="font-serif-display text-2xl mt-6 leading-tight">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="enquire" className="section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5 img-zoom h-[500px] md:h-full">
            <img src={IMAGES.weddingCouple} alt="Wedding couple" className="w-full h-full object-cover" />
          </div>
          <div className="md:col-span-7"><EnquiryForm type="wedding" /></div>
        </div>
      </section>
    </div>
  );
}
